import { computed, nextTick, Ref, ref } from "vue";
import { AuthenticationState } from "../models/authentication-state";
import { postLoginData, postLogout, postForgottenPassword, postPasswordReset } from "../authentication-api";
import { LoginRequest } from "../models/login-request";
import { useMountAuthentication } from "./use-mount-authentication";
import { useMountLogout } from "./use-mount-logout";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { AuthenticationAnalytics } from "../analytics/analytics-authentication";
import { logException } from "@sazka/utils/logger/use-app-logger";
import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";

const isAuthModalOpen = ref(false);
const isLogoutModalOpen = ref(false);
const isConfirmationModalOpen = ref(false);

const emailRef = ref("");
const passwordRef = ref("");
const phoneRef = ref("");
const phoneMasked = ref("");
const errorMessage: Ref<string> = ref(null);
const infoMessage: Ref<string> = ref(null);
const currentState = ref<AuthenticationState>(AuthenticationState.Login);

export function useAuthentication() {
    //#region General
    function initializeInterface() {
        if (!window.authentication) {
            window.authentication = {
                ngLoginApi: {
                    success: null,
                },
                openLoginModal,
            };
        }
    }

    async function openLoginModal(useLoader = true) {
        eventBus.emit(GlobalEvents.WebLoginStarted);

        await ngFrameworkLoaded();
        await useMountAuthentication(useLoader);

        clearLoginData();

        nextTick(() => {
            isAuthModalOpen.value = true;
        });
    }

    function clearLoginData() {
        emailRef.value = "";
        passwordRef.value = "";
        phoneRef.value = "";
        phoneMasked.value = null;
        errorMessage.value = null;
        infoMessage.value = null;
        currentState.value = AuthenticationState.Login;
    }

    const countdown = ref(0);
    const smsInfo = computed(
        () =>
            "Znovu jsme vám poslali kód na registrované telefonní číslo. Další nový kód může být zaslán za " +
            countdown.value +
            " s."
    );

    function startCountdown() {
        if (countdown.value > 0) return;
        countdown.value = 15;
        const interval = setInterval(() => {
            countdown.value--;
            infoMessage.value = smsInfo.value;
            if (countdown.value <= 0) {
                clearInterval(interval);
                infoMessage.value = null;
            }
        }, 1000);
    }

    function safeParseJson(jsonString: string) {
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return null;
        }
    }

    function handleAuthenticationError(error, errorInfo: string) {
        logException("Authentication error", {
            errorMessage: errorInfo,
            error: error,
        });

        const errorResponse = error.message?.response?.data ?? error.message;
        const errorArray = safeParseJson(errorResponse);
        AuthenticationAnalytics.pushLoginFailure();

        if (!errorArray || !(errorArray instanceof Array) || errorArray.length === 0) {
            errorMessage.value = "Váš požadavek nyní nelze zpracovat. Zkuste to prosím později.";
            return;
        }

        switch (errorArray[0] as string) {
            case "INVALID_CREDENTIALS":
                errorMessage.value =
                    "Přístup byl odepřen. Zkontrolujte své přihlašovací údaje a zkuste se přihlásit znovu.";
                break;
            case "MISSING_DATA":
                handleMissingData(errorArray);
                break;
            case "MAX_NUMBER_OF_REQUESTS":
                errorMessage.value =
                    "Dosáhli jste maximálního počtu pokusů pro zaslání ověřovacího kódu. Zkuste to prosím později.";
                break;
            case "INCORRECT_OTP":
                errorMessage.value = "Zadali jste nesprávný ověřovací kód. Zadejte prosím údaj znovu.";
                break;
            case "EXPIRED_OTP":
                setState(
                    AuthenticationState.LoginOtp,
                    "Platnost vašeho bezpečnostního kódu vypršela. Požádejte si o nový prostřednictvím odkazu “Poslat znovu” a opakujte zadání."
                );
                break;
            case "SUSPENDED_ACCOUNT":
                errorMessage.value =
                    "Přístup k vašemu účtu nebyl umožněn. Pro více informací se spojte s naším kontaktním centrem.";
                break;
            case "UNFINISHED_REGISTRATION":
                errorMessage.value = `Váš dočasný účet byl uzavřen, protože vypršela 30denní lhůta k jeho dokončení. <a class="authentication__link" href="/registrace">Zaregistrujte se prosím znovu.</a>`;
                break;
            case "RG_DAILY_LOGIN_TIME_EXCEEDED":
                errorMessage.value = "Překročili jste nastavený limit pro dobu přihlášení. Přihlásit se můžete za: "; //TODO
                break;
            case "EMPTY_FIELD":
                errorMessage.value = "Vyplňte prosím všechna povinná pole.";
                break;
            case "PASSWORD_NOT_DIFFERENT":
                errorMessage.value = "Vaše nové heslo bohužel nemůže být kvůli bezpečnosti stejné jako to předchozí.";
                break;
            case "TOO_MANY_UNSUCCESSFUL_LOGIN_ATTEMPTS":
                errorMessage.value =
                    "Počet neúspěšných pokusů o přihlášení překročil povolený limit. Zkuste to prosím později.";
                break;
            case "GENERAL_ERROR":
            default:
                errorMessage.value = "Váš požadavek nyní nelze zpracovat. Zkuste to prosím později.";
                break;
        }
    }

    function setState(state: AuthenticationState = null, error: string = null) {
        currentState.value = state | currentState.value;
        errorMessage.value = error;
    }
    //#endregion

    //#region Login
    async function login(email: string, password: string, verificationCode: string = null, newPassword: string = null) {
        emailRef.value = email;
        passwordRef.value = password;

        errorMessage.value = null;
        infoMessage.value = null;

        try {
            const loginRequest = createLoginRequest(email, password, verificationCode, newPassword);
            const response = await postLoginData(loginRequest);

            await window.authentication.ngLoginApi.success(response.sessionToken, response.playerId);
            loginSuccess();
        } catch (error) {
            handleAuthenticationError(error, "Login error");
        }
    }

    function loginAborted() {
        eventBus.emit(GlobalEvents.WebLoginAborted);
    }

    function createLoginRequest(
        email: string,
        password: string,
        verificationCode: string = null,
        newPassword: string = null
    ): LoginRequest {
        const loginRequest: LoginRequest = {
            email: email,
            password: password,
        };

        if (currentState.value === AuthenticationState.LoginOtp) {
            loginRequest.challenges = {
                oneTimePassword: verificationCode,
            };
        }

        if (
            currentState.value === AuthenticationState.ForcedPasswordReset ||
            currentState.value === AuthenticationState.ForcedPasswordResetOtp
        ) {
            loginRequest.challenges = {
                oneTimePassword: verificationCode,
                newPassword: newPassword,
            };
        }

        return loginRequest;
    }

    function loginSuccess() {
        isAuthModalOpen.value = false;
        clearLoginData();

        AuthenticationAnalytics.pushLoginSuccess();
    }

    async function resendLoginOtp() {
        if (countdown.value > 0) return;

        try {
            await login(emailRef.value, passwordRef.value);
            if (!errorMessage.value) {
                startCountdown();
                infoMessage.value = smsInfo.value;
            }
        } catch (error) {
            handleAuthenticationError(error, "Resend OTP error");
        }
    }

    function handleMissingData(error: string[]) {
        const additionalInfo = error[1] || null;
        if (additionalInfo) {
            const additionalInfoJson = JSON.parse(additionalInfo);

            const isOtpRequired: boolean = additionalInfoJson.MissingData.includes("OneTimePassword");
            const isNewPasswordRequired: boolean = additionalInfoJson.MissingData.includes("NewPassword");

            if (isOtpRequired && isNewPasswordRequired) {
                phoneMasked.value = additionalInfoJson.PhoneNumber;
                setState(AuthenticationState.ForcedPasswordResetOtp);
            } else if (isNewPasswordRequired) {
                setState(AuthenticationState.ForcedPasswordReset);
            } else if (isOtpRequired) {
                phoneMasked.value = additionalInfoJson.PhoneNumber;
                setState(AuthenticationState.LoginOtp);
            }
        }
    }
    //#endregion

    //#region Password reset
    function showForgottenPassword() {
        setState(AuthenticationState.ForgottenPassword);
    }

    async function forgottenPassword(email: string, phone: string) {
        const phoneData = "420" + phone.replaceAll(" ", "");
        emailRef.value = email;
        phoneRef.value = phone;
        errorMessage.value = null;
        infoMessage.value = null;

        try {
            await postForgottenPassword({ email: email, mobile: phoneData });
            passwordRef.value = null;
            currentState.value = AuthenticationState.PasswordReset;
        } catch (error) {
            handleAuthenticationError(error, "Forgotten password error");
        }
    }

    async function passwordResetResend() {
        if (isConfirmationModalOpen.value) return;
        if (countdown.value > 0) return;

        try {
            await forgottenPassword(emailRef.value, phoneRef.value);
            if (!errorMessage.value) {
                startCountdown();
                infoMessage.value = smsInfo.value;
            }
        } catch (error) {
            handleAuthenticationError(error, "Resend password reset error");
        }
    }

    async function passwordReset(email: string, newPassword: string, verificationCode: string) {
        if (isConfirmationModalOpen.value) return;

        try {
            await postPasswordReset({
                email: email,
                newPassword: newPassword,
                challenges: { oneTimePassword: verificationCode },
            });
            errorMessage.value = null;
            infoMessage.value = null;
            passwordRef.value = null;

            openConfirmationModal();
        } catch (error) {
            handleAuthenticationError(error, "Password reset error");
        }
    }
    //#endregion

    // #region Confirmation Modal
    function openConfirmationModal() {
        isConfirmationModalOpen.value = true;
    }

    function closeConfirmationModal() {
        isConfirmationModalOpen.value = false;
        currentState.value = AuthenticationState.Login;
    }
    // #endregion

    // #region Logout
    async function logout() {
        await useMountLogout();
        clearLoginData();

        nextTick(() => {
            isLogoutModalOpen.value = true;
            isAuthModalOpen.value = false;
        });
        try {
            await postLogout();
        } finally {
            ng_fw.postLogout();
        }
    }
    // #endregion

    return {
        isAuthModalOpen,
        isLogoutModalOpen,
        isConfirmationModalOpen,
        email: emailRef,
        password: passwordRef,
        phoneMasked,
        currentState,
        initializeInterface,
        login,
        loginAborted,
        resendLoginOtp,
        showForgottenPassword,
        forgottenPassword,
        passwordReset,
        passwordResetResend,
        openLoginModal,
        logout,
        clearLoginData,
        closeConfirmationModal,
        errorMessage,
        infoMessage,
    };
}
